import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ObjectBaseComponent} from 'features/core/shared/object/mvs-object-base/object-base.component';

/**
 * Root ObjectBaseComponent for `cp.ProvisionAgent` (agent with a provision and
 * reserve account) — layer 1 of the three-layer pattern from PAT §L.2.
 *
 * Repo original (adopted exactly): `features/feature-bm/bm/component/object-components/
 * bm-billing-area-object-component/bm-billing-area-object.component.ts|html`. The root OBC
 * loads the `dto` via `ObjectBaseComponent`, holds `uiMode`, and BRANCHES in the template only by
 * view mode:
 *   view/base  -> {@link CpProvisionAgentBaseComponent}  (navigation items, 04-frontend.md §7)
 *   view/full  -> {@link CpProvisionAgentFullComponent}
 *   view/side  -> {@link CpProvisionAgentSideComponent}
 *   shared/    -> the six detail components per navItem
 *
 * REACHABILITY (04-frontend.md §7.3 / §8) — both paths lead here:
 *   1. `<mvs-object [objectType]="'cp.ProvisionAgent'" [objectId]="id"/>` — the registry
 *      CP_ENTITY_PROVIDER resolves the alias to this component
 *      (`objectComponent: [{mode: MvsCrudModeEnum.update, component: CpProvisionAgentObjectComponent}]`).
 *   2. Route `/#/cp/dashboard/agents` — {@link CpProvisionAgentsComponent} renders exactly this
 *      `<mvs-object>` in the workbench detail pane.
 * It is the ONLY cp entity with its own OBC; the remaining 24 aliases deliberately fall back to
 * the generic CRUD object (PAT §L.3).
 *
 * DIALOG MECHANISM (design gate finding B-9, capability-matrix.md §5) — MANDATORY DOCUMENTATION:
 * the action dialogs "Manuelle Buchung" (POST /cp/provisionAgentTransactions/manualBooking) and
 * "Umbuchung" (POST /cp/provisionAgentTransactions/transfer) contain NO hand-written
 * form HTML. What is used is the repo-documented TRANSIENT FORM WIDGET:
 * an `MvsFormDto` built in the frontend is passed to `<mvs-widget>` via
 * `WidgetFactory.createWidget(alias, name, 'form', 'transient', 'transient', objectType,
 *  WidgetDataParam.create('form', mvsFormDto))`; the captured values come back via
 * `(onGenericEvent)` / `MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE`
 * together with `MvsFormGroup.status`, the write belongs to the caller and goes to the
 * custom endpoint (`CpAgentTransactionService`).
 * Repo evidence for exactly this pattern: `features/feature-core/wf/component/protected-components/
 * wf-process-start/wf-process-start.component.ts` (lines 196–256 + associated template) —
 * start parameters of a process, likewise without an entity behind the form.
 * The root form "Basisdaten" by contrast uses the classic metadata-driven path
 * (`mvs-crud-object` via the object widget, see {@link CpProvisionAgentBaseDataComponent}).
 * Details per dialog are in the Javadoc of the two dialog components.
 */
@Component({
    selector: 'cp-provision-agent-object',
    templateUrl: './cp-provision-agent-object.component.html',
    styleUrls: ['./cp-provision-agent-object.component.scss'],
    standalone: false
})
export class CpProvisionAgentObjectComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    /**
     * German headline of a FAILED object load; null while things are fine.
     *
     * ── THE BLANK PANE THIS FIXES
     *
     * `ObjectBaseComponent.postRefreshObjectError` sets `dto = null` AND `initialized = false`, so
     * the guard `@if (initialized && dto)` in this component's template renders ABSOLUTELY NOTHING
     * — before any navItem pane exists. Measured: opening `9000003052` (an `am.Agent` master record,
     * not a Provisionskonto) leaves the whole object page empty. The base class does raise a toast,
     * but a toast disappears, carries no id, and cannot be read off a screenshot.
     *
     * A blank pane is indistinguishable from a valid empty result, which is the failure class this
     * whole page is built against. So the load error is CAPTURED here and rendered as a standing
     * card naming the status and the id — the same treatment the configuration pane gives its own
     * endpoint errors.
     */
    loadError: string;

    /** Explanatory second line of a failed object load. */
    loadErrorHint: string;

    /** HTTP status of the failed load, shown verbatim so a field report is actionable. */
    loadErrorStatus: number;

    /**
     * Records the failed load, then hands over to the base class unchanged.
     *
     * Deliberately an override rather than an `(onObjectLoadError)` binding: this class IS the
     * `ObjectBaseComponent`, nothing binds to it from outside, and the event has no consumer
     * anywhere in the repository. The `super` call stays first in effect — the toast, the state
     * reset and the event emission all keep happening exactly as before; this only adds a rendering
     * of what already occurred.
     */
    override postRefreshObjectError(error: any) {

        super.postRefreshObjectError(error);

        this.loadErrorStatus = error?.status;

        if (error?.status === 404) {
            this.loadError = 'Zu dieser Id gibt es kein Abrechnungskonto.';
            this.loadErrorHint = 'Die geöffnete Id ist möglicherweise ein Vermittlerstammsatz '
                + '(am.Agent) und nicht das Provisionskonto (cp.ProvisionAgent) — das sind '
                + 'verschiedene Objekte mit verschiedenen Ids. Möglich ist auch, dass das '
                + 'Abrechnungskonto gelöscht wurde.';
            return;
        }

        if (error?.status === 403) {
            this.loadError = 'Keine Berechtigung für dieses Abrechnungskonto.';
            this.loadErrorHint = 'Der Server hat den Lesezugriff auf das Objekt abgelehnt.';
            return;
        }

        if (!error?.status) {
            this.loadError = 'Der Server ist nicht erreichbar.';
            this.loadErrorHint = 'Die Anfrage kam nicht an — Netzwerk oder Backend nicht verfügbar.';
            return;
        }

        this.loadError = 'Das Abrechnungskonto konnte nicht geladen werden.';
        this.loadErrorHint = 'Der Server hat mit HTTP ' + error.status + ' geantwortet.';
    }

    /**
     * The loaded object has changed.
     *
     * Deliberately EMPTY (as in the bm original): the view components get `dto` and
     * `objectIdentifier` as bindings and rebuild their widgets themselves. `refreshObject()` must
     * NEVER be called here (infinite loop, rule G2.5).
     */
    onObjectChanged() {
    }

    ngOnChanges(changes: SimpleChanges) {
        // A new id is a new question — the previous failure must not survive it.
        if (changes['objectIdentifier'] || changes['objectId']) {
            this.loadError = null;
            this.loadErrorHint = null;
            this.loadErrorStatus = null;
        }
        super.ngOnChanges(changes);
        if (!this.initialized) {
            return;
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

}
